/**
 * Minimal character-level diff for short strings (process names) — no dependency needed.
 * LCS-based, O(n·m); fine for names, do NOT use on large texts.
 *
 * Rendering rule: the "from" line is `same` + `removed` segments in order,
 * the "to" line is `same` + `added` segments in order.
 */

export type DiffSegmentType = "same" | "removed" | "added";

export interface DiffSegment {
  text: string;
  type: DiffSegmentType;
}

/** Character-level diff from `from` → `to`, adjacent same-type characters merged into segments. */
export function diffNames(from: string, to: string): DiffSegment[] {
  const n = from.length;
  const m = to.length;

  // LCS length table.
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = from[i] === to[j]
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  // Walk the table emitting per-character ops, merging runs of the same type.
  const segments: DiffSegment[] = [];
  const push = (type: DiffSegmentType, ch: string) => {
    const last = segments[segments.length - 1];
    if (last && last.type === type) last.text += ch;
    else segments.push({ text: ch, type });
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (from[i] === to[j]) {
      push("same", from[i]);
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      push("removed", from[i]);
      i++;
    } else {
      push("added", to[j]);
      j++;
    }
  }
  while (i < n) push("removed", from[i++]);
  while (j < m) push("added", to[j++]);

  return segments;
}
